/* ============================================================================
   DESKTOP NAV — OPEN THE DROPDOWN ON HOVER
   ============================================================================
   Mike, 2026-09-12: "you have to click on it to make the drop-down happen, it
   should be on mouse-over."

   Dawn builds the dropdown out of <details>/<summary>, which is click-only by
   definition, and there is no CSS route to it: a closed <details> does not
   render its children at all, so :hover cannot reveal them. Hence a script.

   ⚠️ THIS IS ADDITIVE, NOT A REPLACEMENT. Dawn's own HeaderMenu custom element
   keeps running — it still handles the toggle event, close-on-focus-out and the
   header's preventHide flag. All this does is set and clear the open attribute,
   which is the same thing a click does, so every one of those handlers fires
   normally. Do not "simplify" this by unbinding Dawn's element.

   ⛔ HOVER IS DESKTOP-ONLY AND THAT IS DELIBERATE. The media query demands a
   fine pointer that can actually hover. On a phone or a tablet the first tap
   would open the menu and the second would follow a link, which is the classic
   way a touch user loses a tap — and below 990px this nav is the drawer anyway,
   which is a different mechanism entirely.
   ============================================================================ */
(() => {
  const DESKTOP = '(min-width: 990px) and (hover: hover) and (pointer: fine)';
  const mq = window.matchMedia(DESKTOP);

  /* Long enough to cross the few pixels between the summary and the panel
     without the menu snapping shut, short enough not to feel stuck. */
  const CLOSE_DELAY = 140;

  const summaryOf = (details) => details.querySelector('summary');

  const open = (details) => {
    if (details.open) return;
    details.open = true;
    const s = summaryOf(details);
    if (s) s.setAttribute('aria-expanded', 'true');
  };

  const close = (details) => {
    if (!details.open) return;
    details.removeAttribute('open');
    const s = summaryOf(details);
    if (s) s.setAttribute('aria-expanded', 'false');
  };

  const wire = () => {
    document.querySelectorAll('.header__inline-menu header-menu').forEach((host) => {
      if (host.dataset.drbHover === 'true') return;
      host.dataset.drbHover = 'true';

      const details = host.querySelector('details');
      if (!details) return;

      let timer = null;

      /* ⚠️ BOUND TO <header-menu>, NOT TO THE SUMMARY. The panel is a child of
         the same element, so moving the pointer down into the dropdown never
         leaves the box and never fires pointerleave. Bind it to the summary and
         the menu closes the instant you reach for an item in it. */
      host.addEventListener('pointerenter', (event) => {
        if (!mq.matches || event.pointerType === 'touch') return;
        window.clearTimeout(timer);
        open(details);
      });

      host.addEventListener('pointerleave', (event) => {
        if (!mq.matches || event.pointerType === 'touch') return;
        window.clearTimeout(timer);
        timer = window.setTimeout(() => close(details), CLOSE_DELAY);
      });

      /* On a hover device the pointer owns the open state, so a mouse click on
         the summary should not toggle it shut underneath the pointer — the menu
         would then stay closed until you left and came back.

         ⚠️ event.detail IS THE KEYBOARD TEST, and it has to stay. Enter and
         Space on a focused <summary> both arrive here as a click with detail 0;
         a real mouse click has detail 1 or more. Without that check this would
         swallow the only way a keyboard user can open the menu. */
      details.addEventListener('click', (event) => {
        if (!mq.matches) return;
        if (event.detail === 0) return;
        if (!event.target.closest('summary')) return;
        event.preventDefault();
      });
    });
  };

  /* Leaving the hover range with a menu still open would strand it open, since
     nothing on a touch screen is going to fire pointerleave. */
  const onChange = () => {
    if (mq.matches) return;
    document.querySelectorAll('.header__inline-menu header-menu details[open]').forEach(close);
  };

  if (mq.addEventListener) mq.addEventListener('change', onChange);
  else if (mq.addListener) mq.addListener(onChange);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', wire, { once: true });
  } else {
    wire();
  }

  document.addEventListener('shopify:section:load', wire);
})();

/* ===========================================================================
   THE HASH IS FOR THE JUMP, NOT FOR THE ADDRESS BAR
   ===========================================================================
   Mike, 2026-09-12: "When I reload the page it always brings me to a different
   anchor. It never brings me back up to the top of the site."

   Nothing is broken — it is the browser doing exactly what it is told. The whole
   homepage is one page and the nav is anchors, so the first time anyone uses the
   menu or a hero button the URL becomes /#study or /#practitioners and STAYS
   that way. Every reload from then on re-honours it, and which section you land
   on depends on which link you last happened to press.

   ⚠️ THAT TRADE IS DIFFERENT HERE THAN ON A NORMAL SITE. On a documentation page
   a sticky hash is a feature: it is a deep link worth keeping. On a one-page
   storefront the "sections" are not destinations, they are the page. The hash
   has no value once the scroll has happened, and it costs a predictable reload.

   So the jump runs normally and the hash is swept out of the address bar a beat
   later, leaving the scroll position exactly where it is. The link still works,
   the back button still works, and a reload goes back to the top.

   ⚠️ THE DELAY IS LOAD-BEARING. scroll-behavior is smooth, so the scroll is still
   in flight when the click handler returns — replacing the URL immediately
   cancels it in Safari and the page simply does not move. 900ms is comfortably
   past the end of a full-page smooth scroll.

   ⛔ NOT history.scrollRestoration = 'manual'. That would also force the top of
   the page on every BACK navigation, so returning from a product page would
   throw away the reader's position. This touches the hash and nothing else.
   =========================================================================== */
(() => {
  const strip = () => {
    if (!window.location.hash) return;
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
  };

  /* A hash already in the URL on arrival — a reload, or a shared link. Let the
     browser do its jump first, then clean up so the NEXT reload starts at the
     top. */
  if (window.location.hash) window.setTimeout(strip, 900);

  document.addEventListener('click', (event) => {
    const link = event.target.closest('a[href*="#"]');
    if (!link) return;
    /* Same document only. A hash on another page is somebody else's business,
       and so is a bare "#" used as a placeholder. */
    const href = link.getAttribute('href') || '';
    if (href === '#' || href.indexOf('#') < 0) return;
    if (link.origin !== window.location.origin) return;
    if (link.pathname !== window.location.pathname) return;

    window.setTimeout(strip, 900);
  });
})();

/* ============================================================================
   THE MOBILE DRAWER'S TABS
   Markup: snippets/drb-drawer.liquid.

   ⚠️ THIS DOES NOT OPEN OR CLOSE THE DRAWER. Dawn's menu-drawer.js owns that,
   along with the overlay, the focus trap and the body scroll lock, and it binds
   to the <details> and #menu-drawer that the snippet deliberately keeps. All
   this does is swap which panel is visible inside it.

   ⛔ role="tablist" IS A PROMISE AND THIS IS WHERE IT IS KEPT. A tablist that
   does not answer arrow keys, and leaves every tab in the tab order, is worse
   than no roles at all: it tells a screen reader to expect behaviour that is
   not there. Left and right move, Home and End jump, and only the selected tab
   is tabbable.
   ============================================================================ */
(() => {
  document.querySelectorAll('[data-drb-drawer]').forEach((root) => {
    if (root.dataset.drbReady === 'true') return;
    root.dataset.drbReady = 'true';

    const tabs = Array.from(root.querySelectorAll('[data-drb-drawer-tab]'));
    const panels = Array.from(root.querySelectorAll('[data-drb-drawer-panel]'));
    if (tabs.length < 2) return;

    /* ⛔ A PANEL THAT SCROLLS HAS TO SAY SO. [Mike, 2026-09-30] "on the science
       tab on mobile you don't know that there's more after What Is A Synbiotic.
       It just kind of ends there... people aren't going to know."

       He is right, and it is not a general problem — it is exactly two panels.
       Shop and Practitioners hold three rows each and fit; Science holds nine
       and About seven, so both run past the fold with no edge, no cut-off row
       and nothing to suggest a gesture would do anything.

       ⚠️ MEASURED, NOT ASSUMED, AND RE-MEASURED ON EVERY SHOW. Which panels
       overflow depends on the phone, the address bar, the font size and how
       many blocks the merchant has added, so hard-coding "Science and About"
       would be right today and wrong the first time someone edits the menu.
       scrollHeight against clientHeight is the only honest test.

       ⚠️ AND IT RUNS AFTER THE PANEL IS VISIBLE. A hidden element measures
       zero for both, so checking before the hidden flag flips says every panel
       fits. */
    /* ⛔ COUNT HIDDEN ROWS, DO NOT MEASURE PIXELS. [Mike, 2026-09-30, twice]
       "the shop one should definitely not have a scroll down, it looks really
       bad... it fit perfectly before this."

       ⚠️ NO PIXEL THRESHOLD CAN SEPARATE THESE PANELS, which is why 8px and
       then 40px both failed. Shop carries a `feature` block — the large promo
       tile with its own image — so it is genuinely taller than its box by a
       lot, while still showing everything a reader needs. Science and About
       are tall for a different reason: they have rows nobody can see.

       ⭐ So measure the thing the pill actually promises. "More below" claims
       there are further ITEMS, and the honest test is whether a whole row
       starts past the bottom edge. On Shop the promo tile begins above the
       fold and merely runs past it, so nothing is undiscoverable and no cue
       appears. On Science four rows start below it and the cue is earned.

       ⚠️ The feature tile is excluded from the count on purpose. It is a
       promo, not a menu item, and a cue that fires because a picture is
       clipped is the same false promise in a different costume. */
    const gauge = (p) => {
      if (p.hidden) return;
      const edge = p.getBoundingClientRect().bottom;
      const rows = p.querySelectorAll('.drb-panel__row:not(.drb-drawer__feature), .drb-panel__grouprow');
      let buried = 0;
      rows.forEach((r) => {
        if (r.getBoundingClientRect().top >= edge - 8) buried += 1;
      });
      const over = buried > 0;
      p.classList.toggle('is-scrollable', over);
      p.classList.toggle('is-end', over && p.scrollTop + p.clientHeight >= p.scrollHeight - 12);
    };

    panels.forEach((p) => {
      if (p.dataset.drbGauged === 'true') return;
      p.dataset.drbGauged = 'true';
      p.addEventListener('scroll', () => gauge(p), { passive: true });
    });

    const select = (tab, focus) => {
      tabs.forEach((t) => {
        const on = t === tab;
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.setAttribute('tabindex', on ? '0' : '-1');
      });
      panels.forEach((p) => {
        p.hidden = p.id !== tab.getAttribute('aria-controls');
      });
      panels.forEach(gauge);
      /* ⚠️ The strip scrolls sideways, so a tab reached by keyboard can be off
         screen. Bring it back before focusing it. */
      if (tab.scrollIntoView) tab.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      if (focus) tab.focus();
    };

    /* ⛔ THIS LISTENER IS ON THE STRIP, IN THE CAPTURE PHASE, AND IT HAS TO BE.
       Dawn's MenuDrawer binds a close handler to EVERY button inside the drawer
       — global.js, `querySelectorAll('button:not(.localization-selector)...')`
       — so a tab, being a <button>, closed the whole menu instead of switching
       panels. Mike: "whenever you click on another menu option it auto closes
       the whole menu."

       The exclusion list in that selector is a hard-coded set of three class
       names, so the only way to be excluded is to edit stock Dawn, which would
       cost the clean `git diff e4db216 HEAD` delta for one line. Capturing on
       the ancestor runs before the target's own listeners, and stopPropagation
       there means the event never reaches the button Dawn bound to.

       ⚠️ SO THE SWITCH HAS TO HAPPEN HERE TOO. Stopping propagation kills our
       own per-tab listener just as dead as Dawn's, which is why there is no
       longer one. A tab with nothing behind it is an <a>, not a button, carries
       no [data-drb-drawer-tab], and is deliberately left alone — it is a real
       destination and closing the drawer on the way out is correct. */
    root.querySelector('[role="tablist"]').addEventListener(
      'click',
      (event) => {
        const tab = event.target.closest('[data-drb-drawer-tab]');
        if (!tab || !tabs.includes(tab)) return;
        event.stopPropagation();
        event.preventDefault();
        select(tab, false);
      },
      true
    );

    root.querySelector('[role="tablist"]').addEventListener('keydown', (event) => {
      const at = tabs.indexOf(document.activeElement);
      if (at < 0) return;

      let next = null;
      if (event.key === 'ArrowRight') next = tabs[(at + 1) % tabs.length];
      if (event.key === 'ArrowLeft') next = tabs[(at - 1 + tabs.length) % tabs.length];
      if (event.key === 'Home') next = tabs[0];
      if (event.key === 'End') next = tabs[tabs.length - 1];
      if (!next) return;

      event.preventDefault();
      select(next, true);
    });

    /* ⚠️ RE-MEASURE WHEN THE BOX CHANGES, not only when a tab is pressed. The
       drawer opens at whatever height the browser chrome leaves it, and on a
       phone that moves — the address bar collapses on scroll and the panel
       grows by 60-odd pixels. A panel that overflowed on open can stop
       overflowing, and the cue has to go with it or it is pointing at nothing.

       ResizeObserver rather than window.resize: the window does not resize
       when the address bar slides, but the panel does. */
    if ('ResizeObserver' in window) {
      const ro = new ResizeObserver(() => panels.forEach(gauge));
      panels.forEach((p) => ro.observe(p));
    }
  });
})();
